<!-- src/views/management/users.vue -->
<script setup lang="ts">
import ConfirmDialogCustom from '@/components/dialogs/ConfirmDialogCustom.vue'
import LogoutTime from '@/layouts/components/LogoutTime.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'

import { useSnackBar } from '@/utils/stores/snackbar'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VDataTable } from 'vuetify/labs/VDataTable'

definePage({
  meta: { layout: 'blank', action: 'read', subject: 'users' },
})

const snackbarStore = useSnackBar()

/* ── 상단 ‘관리’ 탭 라우팅 동기화 ─────────────────────────── */
type MgmtTab = 'permissions' | 'users' | 'sites'
const mgmtTab = ref<MgmtTab>('users')
const route = useRoute()
const router = useRouter()

function syncTabFromRoute() {
  const p = route.path || ''
  if (p.includes('/manage/permissions')) mgmtTab.value = 'permissions'
  else if (p.includes('/manage/sites'))  mgmtTab.value = 'sites'
  else                                       mgmtTab.value = 'users'
}
onMounted(syncTabFromRoute)
watch(() => route.fullPath, syncTabFromRoute)

watch(mgmtTab, v => {
  const next =
    v === 'permissions' ? '/manage/permissions'
    : v === 'sites'     ? '/manage/sites'
    : '/manage/users'
  if (route.path !== next) router.push(next)
})

/* ── 상단 헤더 hover ─────────────────────────────────────── */
const hover = ref(false)

/* ── 테이블 상태 (편집에서 복귀 시에만 복원) ──────────────── */
const STORAGE_KEY = 'manage-users-list-state'
const RETURN_FLAG = 'manage-users-returning'
const isReturning = sessionStorage.getItem(RETURN_FLAG) === '1'
const savedState = isReturning ? JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') : {}
sessionStorage.removeItem(RETURN_FLAG)
sessionStorage.removeItem(STORAGE_KEY)

const page = ref(savedState.page || 1)
const itemsPerPage = ref(savedState.itemsPerPage || 10)
const selected = ref<any[]>([])
const search = ref(savedState.search || '')

const headers = ref([
  { title: '사용자 이름', key: 'name', width: '12%' },
  { title: '기관',        key: 'siteName', width: '14%' },
  { title: '이메일',      key: 'email', width: '24%' },
  { title: '권한', key: 'realName', width: '10%' }, 
  { title: '진행중 시험', key: 'progressTest', width: '18%' }, 
  { title: '참여한 모든 시험', key: 'allTest', width: '20%' }, 
  { title: '편집',        key: 'actions',     sortable: false, width: '120px' },
])

/* ── 데이터 로드 ─────────────────────────────────────────── */
const { data: usersData, execute: fetchUsers } =
  await useApi(createUrl('/management/users', { query: {} }))

const users = computed(() => {
  const arr = Array.isArray(usersData.value) ? usersData.value : []
  // 만료 사용자 선택 불가 표기
  arr.forEach(el => {
    if (el.status === 'Expired') {
      el.selectable = false
      if (!selected.value.includes(el.id)) selected.value.push(el.id)
    }
  })
  return arr
})

const updateOptions = (options: any) => { page.value = options.page }

/* ── 검색 필터 ───────────────────────────────────────────── */
const filterItems = [
  { value: 'name',     title: '사용자 이름' },
  { value: 'email',    title: '이메일' },
  { value: 'siteName', title: '기관' },
]
const filterItem = ref<string>(savedState.filterItem || 'name')

// 편집 페이지로 이동할 때 현재 상태 저장
const goToEdit = (id: string) => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
    page: page.value,
    search: search.value,
    filterItem: filterItem.value,
    itemsPerPage: itemsPerPage.value,
  }))
  router.push({ name: 'manage-users-edit-id', params: { id } })
}

const filterText = (value: any, query: string, item: any) => {
  return value != null && query != null &&
    typeof value === 'string' &&
    String(item.columns[filterItem.value] || '')
      .toLowerCase()
      .includes(query.toLowerCase())
}

/* ── 액션: 삭제/복원 ─────────────────────────────────────── */
const deleteUser = async (id: string, deleted: 0 | 1) => {
  const res = await $api(`/management/users/${id}`, {
    method: 'DELETE',
    body: { deleted },
  })
  if (res?.code === 'SUCCESS') snackbarStore.showSnackBar('Successfully Update!')
  fetchUsers()
}

/* ── 완전삭제 ─────────────────────────────────────────────── */
const forceDeleteConfirm = ref(false)
const forceDeleteTargetId = ref<string | null>(null)

const openForceDelete = (id: string) => {
  forceDeleteTargetId.value = id
  forceDeleteConfirm.value = true
}

const onForceDeleteConfirm = async (confirmed: boolean) => {
  if (!confirmed) {
    forceDeleteTargetId.value = null
    return
  }
  await $api(`/management/users/${forceDeleteTargetId.value}/force`, { method: 'DELETE' })
  snackbarStore.showSnackBar('완전삭제 되었습니다.')
  forceDeleteTargetId.value = null
  fetchUsers()
}

/* ── 보조 포맷터 ─────────────────────────────────────────── */
const getSiteName = (u: any) => u?.siteName || u?.site?.name || '-'
const getProgressText = (u: any) => {
  const done  = u?.done ?? u?.progress?.done ?? u?.answered ?? 0
  const total = u?.total ?? u?.progress?.total ?? u?.questionTotal ?? 0
  const last  = u?.lastInputAt ?? u?.lastAnsweredAt ?? ''
  const p = total ? `${done}/${total}` : '-'
  return { p, last }
}
const attachmentCount = (u: any) => u?.attachments ?? u?.attachCount ?? 0
</script>

<template>
  <!-- 상단 헤더 -->
  <VCard class="topbar-card" style="border-radius:0;">
  <VCardText class="d-flex justify-space-between align-between py-2">
    <!-- 👇 로고 버튼 (왼쪽) -->
    <div
      class="d-flex align-center cursor-pointer"
      @click="router.push('/projects')"
    >
      <img
        src="/images/home_logo.png"
        alt="홈"
        width="87"
        height="30"
        style="display:block"
      />
    </div>

    <!-- 오른쪽 기존 요소 -->
    <div class="d-flex align-center">
      <LogoutTime class="me-2" />
      <UserProfile />
      <div class="divider-vert" />
      <div
        class="mgmt-quick d-flex align-center px-2 py-1"
        :style="{ backgroundColor: hover ? 'rgba(0,0,0,0.05)' : 'transparent' }"
        @click="router.push('/manage/permissions')"
        @mouseover="hover = true"
        @mouseleave="hover = false"
      >
        <img src="/images/settings.png" alt="관리" class="icon-20 me-1" />
        <span class="text-gray-600">관리</span>
      </div>
    </div>
  </VCardText>
</VCard>


  <!-- 페이지 타이틀 -->
  <div class="page-title-wrap">
    <h2 class="page-title">관리</h2>
  </div>

  <!-- 관리 탭 -->
  <div class="mgmt-tabs">
    <VBtnToggle v-model="mgmtTab" class="tab-toggle" mandatory divided borderless>
      <VBtn :value="'permissions'" class="tab-btn">권한 관리</VBtn>
      <VBtn :value="'users'"       class="tab-btn">전체 사용자 관리</VBtn>
      <VBtn :value="'sites'"       class="tab-btn">전체 기관 관리</VBtn>
    </VBtnToggle>
  </div>

  <!-- 본문 -->
  <VRow class="page-wrap">
    <VCol cols="12">
      <VCard elevation="1">
        <!-- 리스트 헤더(좌: 타이틀 / 우: 필터·검색) -->
        <div class="list-head">
          <div class="lh-left">전체 사용자 목록</div>
          <div class="lh-right">
            <AppSelect v-model="filterItem" :items="filterItems" style="inline-size:140px;" />
            <AppTextField
              v-model="search"
              class="pe-2"
              style="inline-size:220px;"
              :placeholder="$t('Search') + '...'"
            />
            <VBtn
              color="#3857F5"
              class="btn-add"
              append-icon="tabler-plus"
              @click="router.push('/manage/users/create')"
            >
              사용자 등록
            </VBtn>
          </div>
        </div>

        <VCardText class="pt-0">

          <VDataTable
            class="custom-table"
            v-model="selected"
            :headers="headers"
            :items="users"
            :search="search"
            :custom-filter="filterText"
            density="comfortable"
            :items-per-page="itemsPerPage"
            :page="page"
            item-selectable="selectable"
            @update:options="updateOptions"
          >
            <!-- 기관 -->
            <template #item.siteName="{ item }">
              {{ getSiteName(item) }}
            </template>

            <!-- 진행률 / 최종입력 시간 -->
            <template #item.progress="{ item }">
              <div class="prog">
                <a class="linklike">{{ getProgressText(item).p }}</a>
                <span v-if="getProgressText(item).last" class="muted ms-2">
                  · {{ getProgressText(item).last }}
                </span>
              </div>
            </template>


            <!-- 진행률 / 최종입력 시간 -->
            <template #item.progress2="{ item }">
              <div class="prog">
                <a class="linklike">{{ getProgressText(item).p }}</a>
                <span v-if="getProgressText(item).last" class="muted ms-2">
                  · {{ getProgressText(item).last }}
                </span>
              </div>
            </template>
            <!-- 편집(삭제/복원) -->
            <template #item.actions="{ item }">
            <div class="d-flex gap-1">
              <!-- 수정 -->
              <IconBtn
                @click="goToEdit(item.id)"
                title="수정"
              >
                <VIcon icon="tabler-edit" color="primary" />
                <VTooltip activator="parent" location="top">수정</VTooltip>
              </IconBtn>

              <!-- 삭제 -->
              <IconBtn
                v-if="item.deleted != '1' && item.id != '1'"
                @click="deleteUser(item.id, 1)"
              >
                <VIcon icon="tabler-trash" color="error" />
                <VTooltip activator="parent" location="top">삭제</VTooltip>
              </IconBtn>

              <!-- 복원 -->
              <IconBtn
                v-if="item.deleted == '1' && item.id != '1'"
                @click="deleteUser(item.id, 0)"
              >
                <VIcon icon="tabler-restore" color="success" />
                <VTooltip activator="parent" location="top">복원</VTooltip>
              </IconBtn>

              <!-- 완전삭제 -->
              <IconBtn
                v-if="item.deleted == '1' && item.id != '1'"
                @click="openForceDelete(item.id)"
              >
                <VIcon icon="tabler-trash-x" color="error" />
                <VTooltip activator="parent" location="top">완전삭제</VTooltip>
              </IconBtn>
            </div>
          </template>

            <!-- 이름/이메일 삭제선 표시 유지 -->
            <template #item.name="{ item }">
              <span :class="{ deleted: item.deleted != '0' }">{{ item.name }}</span>
            </template>
            <template #item.email="{ item }">
              <span :class="{ deleted: item.deleted != '0' }">{{ item.email }}</span>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <ConfirmDialogCustom
    v-model="forceDeleteConfirm"
    confirmation-question="완전삭제하면 복원할 수 없습니다.<br/>정말 삭제하시겠습니까?"
    confirm-msg=""
    cancel-msg=""
    confirm-color="error"
    @confirm="onForceDeleteConfirm"
  />
</template>

<style scoped>
/* ── 헤더 공통 ─────────────────────────────────────────── */
.divider-vert{
  background-color: rgba(0,0,0,10%);
  block-size: 30px; inline-size: 1px; margin-inline: 15px;
}
.icon-20{ block-size:20px; inline-size:20px; }
.text-gray-600{ color:#4B5563; font-size:14px; }
.mgmt-quick{ border-radius:6px; cursor:pointer; transition:background-color .2s; }

/* ── 페이지 타이틀 ─────────────────────────────────────── */
.page-title-wrap{ padding-block: 50px 0; padding-inline: 34px; }

.page-title{ margin:0;
  border-block-end: 1px solid #DAE6EE; color:#111827; font-size:22px; font-weight:700;
  margin-block-end : 20px;
  padding-block-end: 20px; }

/* ── 탭 공통 ───────────────────────────────────────────── */
.mgmt-tabs{ display:flex; justify-content:flex-start; padding-block: 10px 8px; padding-inline: 34px; }

.tab-toggle{
  border:1px solid #E8ECEF; border-radius:8px; background:#f4f6fa;
  box-shadow:0 3px 8px rgba(201,201,201,25%);
}
.tab-btn{ min-inline-size:160px; }
.tab-btn.v-btn--active{ background:#eef0ff !important; }

/* ── 본문 컨테이너 ─────────────────────────────────────── */
.page-wrap{ margin-block:14px 28px; margin-inline:34px; }

/* 리스트 헤더(카드 상단 툴바) */
.list-head{
  position:relative;
  display:flex; align-items:center; gap:12px;
  padding-block: 16px 10px;
  padding-inline: 16px;
}
.lh-left{ color:#1f2937; font-weight:700; }
.lh-right{ display:flex; align-items:center; gap:8px; margin-inline-start:auto; }

/* 버튼 사이즈 */
.btn-add{ block-size:36px !important; min-block-size:36px !important; }

/* 셀 스타일 */
.deleted{ text-decoration: line-through; }
.prog .linklike{ color:#3857F5; cursor:pointer; text-decoration:none; }
.muted{ color:#94a3b8; font-size:12px; }


.v-data-table .v-table__wrapper > table > thead > tr > th,
.v-data-table .v-table__wrapper > table > tbody > tr > th {
  font-size: 15px;
}



:deep(.custom-table .v-table__wrapper table) {
  th {
    background: #F3F5F8 !important;  /* 밝은 회색 배경 */
    border-block-end: 1px solid #eee;
    color: #909090 !important;       /* 연한 회색 텍스트 */
    font-size: 13px;
    font-weight: 500;
    padding-block: 10px;
    padding-inline: 12px;
  }

  td {
    border-block-end: 1px solid #E8EBEE;
    color: #444;
    font-size: 14px;
    padding-block: 8px;
    padding-inline: 12px;
  }

  tr:hover td {
    background-color: #f9f9fc; /* hover 시 은은한 하이라이트 */
  }
}

:deep(.custom-table .v-badge__badge) {
  border-radius: unset;
  font-size: 8px;
  margin-inline-start: 5px;
  min-inline-size: 10px;
}

:deep(.v-card-item) {
  padding: 12px;
}
</style>
